import React from 'react';
import { CONFIDENCE_FACTOR_CONFIG } from '../miq/MIQView';

/**
 * ProfileCompletePrompt - Modal shown after profile setup
 * Guides user to complete AI Nutrition Goals and Body Composition
 */
const ProfileCompletePrompt = ({
  show,
  onClose,
  getMiqStatusText,
  aiNutritionGoals,
  userProfile,
  metabolicProfile,
  onOpenAiGoals,
  onOpenBodyComposition,
  onNavigateToMIQ
}) => {
  if (!show) return null;
  
  const hasAiGoals = aiNutritionGoals?.intake_complete;
  const hasBodyComp = userProfile?.body_composition?.body_type || metabolicProfile?.somatotype;
  
  return (
    <div className="modal-overlay" style={{ zIndex: 10001 }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', textAlign: 'center' }}>
        {/* Success Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '36px'
          }}>
            ✅
          </div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', color: '#38ef7d' }}>
            Profile Created!
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            Your basic profile is set. Now let's unlock your full metabolic intelligence.
          </p>
        </div>
        
        {/* MIQ Status */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '24px' }}>🧠</span>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#667eea' }}>
              MIQ: {getMiqStatusText().factors}/{CONFIDENCE_FACTOR_CONFIG.TOTAL_FACTORS} factors active
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
            Complete SmartGoals to unlock <strong>+30%</strong> more intelligence
          </p>
        </div>
        
        {/* Next Steps */}
        <div style={{ textAlign: 'left', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
            🎯 Complete These for Full MIQ:
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* SmartGoals */}
            <div 
              onClick={() => {
                onClose();
                onOpenAiGoals();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px',
                background: hasAiGoals ? 'rgba(56, 239, 125, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                border: hasAiGoals ? '1px solid rgba(56, 239, 125, 0.3)' : '2px solid rgba(255, 152, 0, 0.4)',
                borderRadius: '10px',
                cursor: hasAiGoals ? 'default' : 'pointer',
                transition: 'transform 0.2s'
              }}
            >
              <span style={{ fontSize: '24px' }}>{hasAiGoals ? '✅' : '⚡'}</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                  🧠 SmartGoals
                </p>
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {hasAiGoals ? 'Complete!' : 'Personalize your targets • +30% MIQ'}
                </p>
              </div>
              {!hasAiGoals && <span style={{ color: '#ff9800', fontWeight: '600' }}>→</span>}
            </div>
            
            {/* Body Composition */}
            <div 
              onClick={() => {
                if (!hasBodyComp) {
                  onClose();
                  onOpenBodyComposition();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px',
                background: hasBodyComp ? 'rgba(56, 239, 125, 0.1)' : 'rgba(102, 126, 234, 0.08)',
                border: hasBodyComp ? '1px solid rgba(56, 239, 125, 0.3)' : '1px solid rgba(102, 126, 234, 0.2)',
                borderRadius: '10px',
                cursor: hasBodyComp ? 'default' : 'pointer'
              }}
            >
              <span style={{ fontSize: '24px' }}>{hasBodyComp ? '✅' : '📸'}</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                  Body Composition Analysis
                </p>
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {hasBodyComp ? 'Complete!' : 'AI body type detection • +20% MIQ'}
                </p>
              </div>
              {!hasBodyComp && <span style={{ color: '#667eea' }}>→</span>}
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onClose}
            className="btn-secondary"
            style={{ flex: 1 }}
          >
            Later
          </button>
          <button
            onClick={() => {
              onClose();
              if (!hasAiGoals) {
                onOpenAiGoals();
              } else if (!hasBodyComp) {
                onOpenBodyComposition();
              } else {
                onNavigateToMIQ();
              }
            }}
            className="btn-primary"
            style={{ 
              flex: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            {!hasAiGoals ? '🎯 Set AI Goals' : !hasBodyComp ? '📸 Analyze Body' : '🧠 View MIQ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletePrompt;
